import { t } from "@/i18n";
import { Resolution } from "@/types/common";
import {
  ImageModelInfo,
  ImageModelViewInfo,
  VideoModelInfo,
  VideoModelViewInfo,
} from "@/types/mediaArtifact";

/**
 * 模型选择错误类型
 */
export type ModelSelectionError =
  | "modelNotFound"
  | "imageRequired"
  | "onlySingleImageSupported"
  | "imageNotSupported";

/**
 * 模型选择结果类型
 */
export type ModelSelectionResult<T> =
  | { success: true; model: T }
  | { success: false; error: ModelSelectionError };

/**
 * 通用的模型分组方法
 * 按 display_name 分组，合并相同名称的模型的能力标识
 *
 * @param modelInfoList - 原始模型列表
 * @param extractCapabilities - 提取能力标识的函数
 * @param mergeCapabilities - 合并能力标识的函数
 * @returns 分组后的 Map，key 为 display_name，value 为 capabilities
 */
export function groupModelsByDisplayName<
  T extends { display_name: string },
  C extends Record<string, unknown>
>(
  modelInfoList: T[],
  extractCapabilities: (model: T) => C,
  mergeCapabilities: (existing: C, current: C) => void
): Map<string, { capabilities: C }> {
  const grouped = new Map<string, { capabilities: C }>();

  for (const modelInfo of modelInfoList) {
    const displayName = modelInfo.display_name;
    const existing = grouped.get(displayName);

    if (!existing) {
      grouped.set(displayName, {
        capabilities: extractCapabilities(modelInfo),
      });
      continue;
    }

    // 合并能力标识
    mergeCapabilities(existing.capabilities, extractCapabilities(modelInfo));
  }

  return grouped;
}

/**
 * 将 ImageModelInfo 转换为 ImageModelViewInfo
 * 按 display_name 分组并合并能力标识（text2image, image2image, images2image）
 */
export function convertImageModelInfoList(
  modelInfoList: ImageModelInfo[]
): ImageModelViewInfo[] {
  if (modelInfoList.length === 0) {
    return [];
  }

  const grouped = groupModelsByDisplayName(
    modelInfoList,
    (model) => ({
      text2image: model.text2image,
      image2image: model.image2image,
      images2image: model.images2image,
      modelIds: [model.name],
      creditCost: model.price_items?.[0]?.price?.output ?? 0,
    }),
    (existing, current) => {
      existing.text2image ||= current.text2image;
      existing.image2image ||= current.image2image;
      existing.images2image ||= current.images2image;
      existing.modelIds.push(...current.modelIds);
    }
  );

  return Array.from(grouped.entries()).map(([displayName, info]) => {
    const label = t(["model", "image", displayName, "label"], displayName);
    const description = t(["model", "image", displayName, "description"], "");
    return {
      value: displayName,
      label,
      description,
      creditCost: info.capabilities.creditCost,
      modelIds: info.capabilities.modelIds,
      text2image: info.capabilities.text2image,
      image2image: info.capabilities.image2image,
      images2image: info.capabilities.images2image,
    };
  });
}

/**
 * 将 VideoModelInfo 转换为 VideoModelViewInfo
 * 按 display_name 分组并合并能力标识（text2video, image2video, images2video, is_avatar_model）
 */
export function convertVideoModelInfoList(
  modelInfoList: VideoModelInfo[],
  resolution?: Resolution
): VideoModelViewInfo[] {
  if (modelInfoList.length === 0) {
    return [];
  }

  const filteredModels =
    resolution === undefined
      ? modelInfoList
      : modelInfoList.filter((model) =>
          model.price_items?.some(
            (item) => item.properties?.resolution === resolution
          )
        );

  if (filteredModels.length === 0) {
    return [];
  }

  const grouped = groupModelsByDisplayName(
    filteredModels,
    (model) => {
      const priceItem = model.price_items?.find(
        (item) => item.properties?.resolution === resolution
      );

      return {
        text2video: model.text2video,
        image2video: model.image2video,
        images2video: model.images2video,
        is_avatar_model: model.is_avatar_model,
        modelIds: [model.name],
        creditCost: priceItem?.price?.output ?? 0,
        durationOptions: model.duration_options ?? [],
        unitType: priceItem?.unit_type ?? "seconds",
      };
    },
    (existing, current) => {
      existing.text2video ||= current.text2video;
      existing.image2video ||= current.image2video;
      existing.images2video ||= current.images2video;
      // is_avatar_model 只要有一个是 true 就为 true
      existing.is_avatar_model ||= current.is_avatar_model;
      existing.modelIds.push(...current.modelIds);
    }
  );

  return Array.from(grouped.entries()).map(([displayName, info]) => {
    const label = t(["model", "video", displayName, "label"], displayName);
    const description = t(["model", "video", displayName, "description"], "");
    return {
      value: displayName,
      label,
      description,
      creditCost: info.capabilities.creditCost,
      text2video: info.capabilities.text2video,
      image2video: info.capabilities.image2video,
      images2video: info.capabilities.images2video,
      isAvatarModel: info.capabilities.is_avatar_model,
      modelIds: info.capabilities.modelIds,
      durationOptions: info.capabilities.durationOptions,
      unitType: info.capabilities.unitType,
    };
  });
}

/**
 * 根据附件数量和选中的模型 displayName，选择合适的图片模型
 *
 * @param displayName - 用户选中的模型 displayName
 * @param modelInfoList - 原始图片模型列表
 * @param attachmentCount - 附件数量
 * @returns 匹配的模型信息，成功时返回模型，失败时返回错误类型
 */
export function selectImageModel(
  displayName: string,
  modelInfoList: ImageModelInfo[],
  attachmentCount: number
): ModelSelectionResult<ImageModelInfo> {
  // 过滤出 displayName 匹配的模型
  const matchingModels = modelInfoList.filter(
    (model) => model.display_name === displayName
  );

  if (matchingModels.length === 0) {
    return { success: false, error: "modelNotFound" };
  }

  const images2imageModel = matchingModels.find((model) => model.images2image);
  const image2imageModel = matchingModels.find((model) => model.image2image);
  const text2imageModel = matchingModels.find((model) => model.text2image);

  // 根据附件数量选择合适的模型
  if (attachmentCount > 1) {
    if (images2imageModel) {
      return { success: true, model: images2imageModel };
    } else if (image2imageModel) {
      return { success: false, error: "onlySingleImageSupported" };
    } else if (text2imageModel) {
      return { success: false, error: "imageNotSupported" };
    }
  } else if (attachmentCount === 1) {
    if (image2imageModel) {
      return { success: true, model: image2imageModel };
    } else if (text2imageModel) {
      return { success: false, error: "imageNotSupported" };
    }
  } else {
    if (text2imageModel) {
      return { success: true, model: text2imageModel };
    } else if (image2imageModel) {
      return { success: false, error: "imageRequired" };
    }
  }

  return { success: false, error: "modelNotFound" };
}

/**
 * 根据附件数量和选中的模型 displayName，选择合适的视频模型
 *
 * @param displayName - 用户选中的模型 displayName
 * @param modelInfoList - 原始视频模型列表
 * @param attachmentCount - 附件数量
 * @returns 匹配的模型信息，成功时返回模型，失败时返回错误类型
 */
export function selectVideoModel(
  displayName: string,
  modelInfoList: VideoModelInfo[],
  attachmentCount: number
): ModelSelectionResult<VideoModelInfo> {
  // 过滤出 displayName 匹配的模型
  const matchingModels = modelInfoList.filter(
    (model) => model.display_name === displayName
  );

  if (matchingModels.length === 0) {
    return { success: false, error: "modelNotFound" };
  }

  const images2videoModel = matchingModels.find((model) => model.images2video);
  const image2videoModel = matchingModels.find((model) => model.image2video);
  const text2videoModel = matchingModels.find((model) => model.text2video);

  // 根据附件数量选择合适的模型
  if (attachmentCount > 1) {
    if (images2videoModel) {
      return { success: true, model: images2videoModel };
    } else if (image2videoModel) {
      return { success: false, error: "onlySingleImageSupported" };
    } else if (text2videoModel) {
      return { success: false, error: "imageNotSupported" };
    }
  } else if (attachmentCount === 1) {
    if (image2videoModel) {
      return { success: true, model: image2videoModel };
    } else if (text2videoModel) {
      return { success: false, error: "imageNotSupported" };
    }
  } else {
    if (text2videoModel) {
      return { success: true, model: text2videoModel };
    } else if (image2videoModel) {
      return { success: false, error: "imageRequired" };
    }
  }

  return { success: false, error: "modelNotFound" };
}
